// src/pages/GiftCardPage.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuthContext } from '../context/AuthContext';
import GiftCardPaymentModal from '../components/wallet/GiftCardPaymentModal';
import { convertToPersianNumbers } from '../utils/PersianNumberUtils';
import { Gift, CreditCard, MessageSquare, Download } from 'lucide-react';
import { jsPDF } from 'jspdf';

const SUGGESTED_AMOUNTS = [500000, 1000000, 2000000, 5000000];
const GIFT_CARD_IMAGES = [
  { id: 1, url: '/images/giftcards/1-1.png' },
  { id: 2, url: '/images/giftcards/2-1.png' },
  { id: 3, url: '/images/giftcards/3-1.png' },
  { id: 4, url: '/images/giftcards/4-1.png' },
];

const QR_CODE_URL = '/images/q.png';

// مسیر فونت‌ها – اگر Doran-Bold/Black در دسترس نیست، از Vazir استفاده می‌شود
const DORAN_BLACK_URL = '/fonts/Doran-Black.ttf';   // یا مسیر دیگر
const DORAN_BOLD_URL  = '/fonts/Doran-Bold.ttf';
const VAZIR_URL       = 'https://cdn.jsdelivr.net/gh/rastikerdar/vazir-font@v30.1.0/dist/Vazir-Regular.ttf';
const VAZIR_BOLD_URL  = 'https://cdn.jsdelivr.net/gh/rastikerdar/vazir-font@v30.1.0/dist/Vazir-Bold.ttf';

// کش فونت‌ها
let fontCache = {};

const loadFontAsBase64 = async (url) => {
  if (fontCache[url]) return fontCache[url];
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to load font from ${url}`);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      fontCache[url] = base64;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// بارگذاری Doran Black (یا Vazir Bold به‌عنوان جایگزین)
const loadDoranBlack = async () => {
  try { return await loadFontAsBase64(DORAN_BLACK_URL); }
  catch {
    console.warn('Doran Black not found, using Vazir Bold as fallback.');
    return await loadFontAsBase64(VAZIR_BOLD_URL);
  }
};

// بارگذاری Doran Bold (یا Vazir Bold)
const loadDoranBold = async () => {
  try { return await loadFontAsBase64(DORAN_BOLD_URL); }
  catch {
    console.warn('Doran Bold not found, using Vazir Bold as fallback.');
    return await loadFontAsBase64(VAZIR_BOLD_URL);
  }
};

// بارگذاری Vazir Regular (برای کد)
const loadVazirRegular = async () => {
  return await loadFontAsBase64(VAZIR_URL);
};

// تبدیل تصویر کارت به DataURL (محلی)
const localizeGiftImageUrl = (url) => {
  if (!url) return null;
  const match = url.match(/(\d)-1\.png$/);
  if (match) {
    const idx = parseInt(match[1], 10);
    if (idx >= 1 && idx <= 4) return `/images/giftcards/${idx}-1.png`;
  }
  return url;
};

const fetchImageAsDataURL = async (url) => {
  const finalUrl = localizeGiftImageUrl(url) || url;
  const response = await fetch(finalUrl);
  if (!response.ok) throw new Error('Failed to fetch image');
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const GiftCardPage = () => {
  const { isLoggedIn, openAuthModal } = useAuthContext();
  const [selectedCard, setSelectedCard] = useState(null);
  const [amount, setAmount] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [giftCard, setGiftCard] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeDot, setActiveDot] = useState(0);
  const carouselRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handleSelectCard = (card) => setSelectedCard(card);
  const handleAmountSelect = (val) => setAmount(val.toString());

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    const cards = carousel.querySelectorAll('[data-card-id]');
    if (cards.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const id = parseInt(entry.target.getAttribute('data-card-id'), 10);
            const index = GIFT_CARD_IMAGES.findIndex(c => c.id === id);
            if (index !== -1) setActiveDot(index);
          }
        });
      },
      { root: carousel, threshold: 0.6 }
    );
    cards.forEach(card => observer.observe(card));
    return () => observer.disconnect();
  }, []);

  const handleOpenPayment = () => {
    if (!isLoggedIn) { openAuthModal(); return; }
    if (!selectedCard) { alert('لطفاً یک طرح کارت هدیه انتخاب کنید.'); return; }
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      alert('لطفاً مبلغ معتبر انتخاب یا وارد کنید.');
      return;
    }
    setIsModalOpen(true);
  };

  const handleSuccess = (cardData) => {
    setGiftCard(cardData);
    setSelectedCard(null);
    setAmount('');
    setCustomMessage('');
    setIsModalOpen(false);
  };

  const getPublicLink = () => {
    if (!giftCard?.share_uuid) return '';
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || 'https://app.farzaneh.app';
    return `${frontendUrl}/giftcard/${giftCard.share_uuid}/`;
  };

  const fallbackCopyText = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  const copyPublicLink = async () => {
    const link = getPublicLink();
    if (!link) { alert('لینک موجود نیست.'); return; }
    try { await navigator.clipboard.writeText(link); }
    catch { fallbackCopyText(link); }
  };

  const getCardColor = (imageUrl, shareUuid) => {
    if (imageUrl) {
      const match = imageUrl.match(/(\d)-\d\.png/);
      const num = match ? parseInt(match[1]) : 0;
      if (num >= 1 && num <= 4) {
        return ['#0d7c4b', '#293038', '#1e5b9c', '#d4a80c'][num - 1];
      }
    }
    if (shareUuid) {
      const hash = shareUuid.split('-')[0];
      let sum = 0;
      for (let i = 0; i < hash.length; i++) sum += hash.charCodeAt(i);
      const variant = (sum % 4) + 1;
      return ['#0d7c4b', '#293038', '#1e5b9c', '#d4a80c'][variant - 1];
    }
    return '#293038';
  };

  // 📦 تولید PDF با چیدمان جدید
  const handleDownloadPDF = useCallback(async () => {
    if (!giftCard) return;
    setIsGeneratingPDF(true);
    try {
      const bgColor = getCardColor(giftCard.image_url, giftCard.share_uuid);
      const formattedAmount = convertToPersianNumbers(parseFloat(giftCard.amount || 0).toLocaleString());

      // بارگذاری هم‌زمان فونت‌ها و تصاویر
      const [doranBlack, doranBold, vazirRegular, cardImage, qrImage] = await Promise.all([
        loadDoranBlack().catch(() => null),
        loadDoranBold().catch(() => null),
        loadVazirRegular().catch(() => null),
        giftCard.image_url ? fetchImageAsDataURL(giftCard.image_url).catch(() => null) : null,
        fetchImageAsDataURL(QR_CODE_URL).catch(() => null),
      ]);

      // در صورت نبود فونت Doran، از Vazir استفاده می‌کنیم
      const titleFont = doranBlack || doranBold || vazirRegular;
      const bodyFont = doranBold || vazirRegular;
      const codeFont = vazirRegular || bodyFont;  // همیشه وزیر، اگر نبود فونت بولد

      if (!titleFont) throw new Error('هیچ فونتی برای عنوان بارگذاری نشد');

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();   // 210mm
      const pageHeight = doc.internal.pageSize.getHeight(); // 297mm

      // پس‌زمینه سفید (پیش‌فرض)

      // ۱. تصویر کارت در نیمهٔ بالایی، خارج از کادر
      let yTop = 20; // شروع از بالا
      if (cardImage) {
        const img = await new Promise((resolve, reject) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = reject;
          el.src = cardImage;
        });
        const imgWidthMm = 60;
        const imgHeightMm = (img.naturalHeight / img.naturalWidth) * imgWidthMm;
        const imgX = (pageWidth - imgWidthMm) / 2;
        doc.addImage(cardImage, 'PNG', imgX, yTop, imgWidthMm, imgHeightMm);
        yTop += imgHeightMm + 10; // فاصله قبل از کادر
      }

      // ۲. کادر رنگی با گوشه‌های گرد در نیمهٔ پایینی
      const boxX = 20;             // حاشیه از چپ
      const boxWidth = pageWidth - 2 * boxX;
      const boxHeight = 100;       // ارتفاع کادر – می‌توانید تنظیم کنید
      const boxY = yTop + 10;      // کمی فاصله از تصویر
      const borderRadius = 5;      // شعاع گردی گوشه‌ها

      // رسم کادر رنگی
      doc.setFillColor(bgColor);
      doc.roundedRect(boxX, boxY, boxWidth, boxHeight, borderRadius, borderRadius, 'F');

      // ۳. متن‌های داخل کادر
      // افزودن فونت‌های لازم
      if (titleFont) {
        doc.addFileToVFS('TitleFont.ttf', titleFont);
        doc.addFont('TitleFont.ttf', 'Title', 'normal');
      }
      if (bodyFont && bodyFont !== titleFont) {
        doc.addFileToVFS('BodyFont.ttf', bodyFont);
        doc.addFont('BodyFont.ttf', 'Body', 'normal');
      }
      if (codeFont && codeFont !== bodyFont && codeFont !== titleFont) {
        doc.addFileToVFS('CodeFont.ttf', codeFont);
        doc.addFont('CodeFont.ttf', 'Code', 'normal');
      }

      const centerX = boxX + boxWidth / 2;
      const textColor = '#ffffff';
      let textY = boxY + 12; // فاصله از بالای کادر

      // عنوان (Doran Black / Vazir Bold) – 10pt
      doc.setFont(titleFont ? 'Title' : 'Body');
      doc.setTextColor(textColor);
      doc.setFontSize(10);
      doc.text('کــارت هــدیــه فـرزانـه', centerX, textY, { align: 'center' });
      textY += 8;

      // مبلغ (Doran Black / Vazir Bold) – 14pt
      doc.setFontSize(14);
      doc.text(`${formattedAmount} تومان`, centerX, textY, { align: 'center' });
      textY += 10;

      // پیام (Doran Bold / Vazir Bold) – 10pt، در صورت وجود
      if (giftCard.message) {
        doc.setFont(bodyFont && bodyFont !== titleFont ? 'Body' : 'Title');
        doc.setFontSize(10);
        const maxWidth = boxWidth - 16; // کمی حاشیه داخل کادر
        const lines = doc.splitTextToSize(`"${giftCard.message}"`, maxWidth);
        doc.text(lines, centerX, textY, { align: 'center' });
        textY += lines.length * 6;
      }

      // کد هدیه با فونت وزیر (Code) – 10pt
      doc.setFont(codeFont ? 'Code' : 'Body');
      doc.setFontSize(10);
      doc.text(`کد: ${giftCard.code}`, centerX, textY, { align: 'center' });
      textY += 12;

      // ۴. QR code در پایین‌ترین بخش صفحه
      const qrTextY = boxY + boxHeight + 15; // فاصله بعد از کادر
      doc.setFont(titleFont ? 'Title' : 'Body');
      doc.setFontSize(9);
      doc.text('برای وارد کردن کد هدیه اسکن کنید', centerX, qrTextY, { align: 'center' });

      if (qrImage) {
        const qrWidthMm = 35;
        const qrImg = await new Promise((resolve, reject) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = reject;
          el.src = qrImage;
        });
        const qrHeightMm = (qrImg.naturalHeight / qrImg.naturalWidth) * qrWidthMm;
        const qrY = qrTextY + 8;
        const qrX = (pageWidth - qrWidthMm) / 2;
        doc.addImage(qrImage, 'PNG', qrX, qrY, qrWidthMm, qrHeightMm);
      }

      doc.save(`gift-card-${giftCard.code || 'farzaneh'}.pdf`);
    } catch (err) {
      console.error('خطا در تولید PDF:', err);
      alert('خطا در تولید PDF. لطفاً دوباره تلاش کنید.');
    } finally {
      setIsGeneratingPDF(false);
    }
  }, [giftCard]);

  // ──────── بقیهٔ UI بدون تغییر ────────
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <Gift size={48} className="text-purple-500 mb-4" />
        <p className="text-gray-600 text-sm mb-4">برای دریافت کارت هدیه، لطفاً وارد شوید.</p>
        <button onClick={openAuthModal} className="bg-purple-600 text-white px-6 py-2 rounded-lg text-sm">
          ورود / ثبت‌نام
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white py-4 px-3 rtl pb-24">
      <div className="max-w-md mx-auto">
        {!giftCard ? (
          <>
            {/* هدر */}
            <div className="bg-white rounded-2xl shadow-sm p-4 mb-6 flex items-center gap-3">
              <Gift size={28} className="text-purple-600 flex-shrink-0" />
              <div>
                <h1 className="text-[16px] font-extrabold text-gray-800">کارت هدیه فرزانه</h1>
                <p className="text-[11px] text-gray-500 mt-0.5">هدیه‌ای ماندگار برای عزیزانتان</p>
              </div>
            </div>

            {/* انتخاب طرح */}
            <div className="bg-white rounded-2xl shadow-sm p-4 mb-4">
              <h2 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                <CreditCard size={18} className="text-purple-500" />
                انتخاب طرح کارت
              </h2>
              <div ref={carouselRef} className="flex overflow-x-auto gap-4 pb-2 scrollbar-thin">
                {GIFT_CARD_IMAGES.map((card) => (
                  <div
                    key={card.id}
                    data-card-id={card.id}
                    onClick={() => handleSelectCard(card)}
                    className={`flex-shrink-0 w-48 cursor-pointer rounded-2xl overflow-hidden border-2 transition-all duration-200 ${
                      selectedCard?.id === card.id
                        ? 'border-purple-600 shadow-lg ring-2 ring-purple-200 scale-105'
                        : 'border-gray-200 shadow-md hover:shadow-lg'
                    }`}
                  >
                    <img src={card.url} alt="طرح کارت هدیه" className="w-full h-auto" />
                    {selectedCard?.id === card.id && (
                      <div className="text-center py-1 bg-purple-100 text-purple-700 text-xs font-bold">
                        انتخاب شده
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-center gap-1 mt-3">
                {GIFT_CARD_IMAGES.map((_, index) => (
                  <span
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all ${
                      activeDot === index ? 'bg-purple-600 w-3 h-3' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* انتخاب مبلغ */}
            <div className="bg-white rounded-2xl shadow-sm p-4 mb-4">
              <label className="text-sm font-bold text-gray-700 block mb-3 flex items-center gap-2">
                <span>💰</span> مبلغ (تومان)
              </label>
              <input
                type="number"
                placeholder="مثلاً ۱۰۰,۰۰۰"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-left focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-50"
              />
              <div className="flex flex-wrap gap-2 mt-3">
                {SUGGESTED_AMOUNTS.map((val) => (
                  <button
                    key={val}
                    onClick={() => handleAmountSelect(val)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                      amount === val.toString()
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {convertToPersianNumbers(val.toLocaleString())} تومان
                  </button>
                ))}
              </div>
            </div>

            {/* متن دلخواه */}
            <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
              <label className="text-sm font-bold text-gray-700 block mb-3 flex items-center gap-2">
                <MessageSquare size={18} className="text-purple-500" />
                متن دلخواه (اختیاری)
              </label>
              <textarea
                rows="3"
                placeholder="پیام خود را برای گیرنده بنویسید..."
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-50 resize-none"
              />
            </div>

            <button
              onClick={handleOpenPayment}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-xl transition duration-200 shadow-md active:scale-95 transform"
            >
              دریافت کارت هدیه
            </button>
          </>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-5 text-center">
            <div className="w-full h-auto mb-4 rounded-xl overflow-hidden shadow-md">
              {giftCard.image_url && (
                <img src={giftCard.image_url} alt="کارت هدیه" className="w-full h-auto" />
              )}
            </div>
            <p className="text-2xl font-extrabold text-purple-700 mb-3">
              {convertToPersianNumbers((giftCard.amount || 0).toLocaleString())} تومان
            </p>
            {giftCard.message && (
              <div className="bg-purple-50 p-3 rounded-xl my-3 text-gray-700 text-sm italic border-r-4 border-purple-400">
                "{giftCard.message}"
              </div>
            )}
            <div className="bg-gray-100 rounded-xl p-3 text-sm tracking-widest font-mono text-gray-800 my-3 break-all border border-dashed border-gray-300">
              کد: {giftCard.code}
            </div>

            <div className="bg-purple-50 rounded-xl p-3 my-3 text-right">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="text-purple-700 font-bold">لینک هدیه:</span>
                <button
                  onClick={copyPublicLink}
                  className="text-purple-600 hover:text-purple-800 bg-white px-3 py-1 rounded-lg text-xs shadow"
                >
                  کپی لینک
                </button>
              </div>
              <div className="mt-2 text-xs text-gray-600 break-all bg-white p-2 rounded-lg border">
                {getPublicLink() || 'در حال ساخت...'}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                این لینک را برای گیرنده ارسال کنید.
              </p>

              {/* دکمه دریافت PDF */}
              <button
                onClick={handleDownloadPDF}
                disabled={isGeneratingPDF}
                className="w-full mt-4 bg-white border border-purple-300 text-purple-700 font-bold py-2.5 rounded-xl transition hover:bg-purple-50 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Download size={18} />
                {isGeneratingPDF ? 'در حال آماده‌سازی...' : 'دریافت PDF کارت هدیه'}
              </button>
            </div>

            <button
              onClick={() => setGiftCard(null)}
              className="text-purple-600 mt-5 text-sm font-bold underline hover:no-underline"
            >
              + ساخت کارت هدیه جدید
            </button>
          </div>
        )}
      </div>

      <GiftCardPaymentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleSuccess}
        amount={amount}
        selectedCard={selectedCard}
        customMessage={customMessage}
      />
    </div>
  );
};

export default GiftCardPage;